/**
 * The single PrimeVue theme module. PrimeVue runs unstyled and every
 * component is styled here with Tailwind utilities and brand tokens.
 * Do not style PrimeVue anywhere else.
 *
 * Touch targets are at least 48px (min-h-touch) so capture and workshop
 * screens work with gloves.
 */
import type { ButtonPassThroughMethodOptions } from 'primevue/button'
import type { PrimeVuePTOptions } from 'primevue/config'

const focus =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mud-deep'

const field =
  'w-full min-h-touch rounded-xl border border-line bg-paper px-4 text-body text-tarmac placeholder:text-muted ' +
  'disabled:opacity-60 aria-invalid:border-mud-deep ' +
  focus

function buttonRoot({ props }: ButtonPassThroughMethodOptions<unknown>): string {
  const base =
    'inline-flex min-h-touch min-w-touch items-center justify-center gap-2 rounded-xl px-5 font-sans text-body font-semibold ' +
    'transition-colors select-none disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer ' +
    focus
  const size =
    props.size === 'large' ? ' min-h-16 px-6 text-title' : props.size === 'small' ? ' px-3' : ''
  const variant =
    props.variant ??
    (props.text ? 'text' : props.outlined ? 'outlined' : props.link ? 'link' : undefined)
  if (variant === 'link')
    return `${base}${size} min-w-0 px-2 text-mud-deep underline underline-offset-4`
  if (variant === 'text') return `${base}${size} text-tarmac hover:bg-line/50`
  if (variant === 'outlined' || props.severity === 'secondary')
    return `${base}${size} border-2 border-tarmac bg-paper text-tarmac hover:bg-canvas`
  if (props.severity === 'contrast')
    return `${base}${size} bg-tarmac text-canvas hover:bg-tarmac/90`
  // Primary: Mud orange deep with Canvas text (4.6:1, any size).
  return `${base}${size} bg-mud-deep text-canvas hover:bg-rust`
}

export const klunkersPassThrough: PrimeVuePTOptions = {
  button: {
    root: buttonRoot,
    label: 'leading-none',
    icon: 'text-[1.1em]',
    loadingIcon: 'animate-spin',
  },
  inputtext: { root: field },
  textarea: { root: field + ' py-3 min-h-28' },
  password: {
    root: 'relative block w-full',
    pcInputText: { root: field + ' pr-14' },
    maskIcon: 'absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-muted',
    unmaskIcon: 'absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-muted',
  },
  card: {
    root: 'overflow-hidden rounded-2xl border border-line bg-paper text-tarmac',
    body: 'flex flex-col gap-3 p-4',
    title: 'text-title font-semibold',
    subtitle: 'text-small text-muted',
    content: 'text-body',
    footer: 'flex gap-3 pt-2',
  },
  dialog: {
    mask: 'bg-tarmac/60 backdrop-blur-sm',
    root: 'm-4 flex max-h-[90dvh] w-[min(32rem,calc(100vw-2rem))] flex-col rounded-2xl bg-paper text-tarmac shadow-xl',
    header: 'flex items-center justify-between gap-4 border-b border-line px-5 py-4',
    title: 'font-display text-h2',
    content: 'overflow-y-auto px-5 py-4',
    footer: 'flex justify-end gap-3 border-t border-line px-5 py-4',
  },
  toast: {
    root: 'w-[min(24rem,calc(100vw-2rem))]',
    message: ({ props }: { props: { message?: { severity?: string } } }) =>
      'mb-3 rounded-xl border-l-8 bg-paper text-tarmac shadow-lg ' +
      (props.message?.severity === 'error' || props.message?.severity === 'warn'
        ? 'border-mud-deep'
        : props.message?.severity === 'success'
          ? 'border-moss'
          : 'border-amber'),
    messageContent: 'flex items-start gap-3 p-4',
    messageText: 'flex flex-1 flex-col gap-1',
    summary: 'font-semibold',
    detail: 'text-small text-muted',
    closeButton:
      'min-h-touch min-w-touch -m-3 inline-flex items-center justify-center rounded-xl ' + focus,
  },
  message: {
    root: ({ props }: { props: { severity?: string } }) =>
      'rounded-xl border px-4 py-3 text-body ' +
      (props.severity === 'error' || props.severity === 'warn'
        ? 'border-mud-deep bg-paper text-mud-deep'
        : props.severity === 'success'
          ? 'border-moss bg-paper text-tarmac'
          : 'border-line bg-paper text-tarmac'),
    content: 'flex items-center gap-2',
    text: 'font-medium',
  },
}
